import React, { useState } from 'react';
import { Button } from '../../ui/common/Button';
import { Modal } from '../../ui/common/Modal';
import type { ConnectableSource } from '../../domain/sourceConnection';
import { providerSetupGuide, type SetupGuideStep } from './providerSetupGuides';

export interface ProviderSetupGuideModalProps {
  source: ConnectableSource;
  isOpen: boolean;
  onClose: () => void;
}

async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const CopyableBlock: React.FC<{ label?: string; text: string }> = ({ label, text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
      >
        {label ? (
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--z-text-2)' }}>{label}</span>
        ) : (
          <span />
        )}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            void copyText(text).then((ok) => {
              setCopied(ok);
              if (ok) window.setTimeout(() => setCopied(false), 1800);
            });
          }}
        >
          {copied ? 'Copiado' : 'Copiar'}
        </Button>
      </div>
      <pre
        style={{
          margin: 0,
          padding: '10px 12px',
          borderRadius: 8,
          background: 'var(--z-fill)',
          border: '1px solid var(--z-border)',
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 12,
          lineHeight: 1.5,
          overflowX: 'auto',
          whiteSpace: 'pre',
          color: 'var(--z-text)',
        }}
      >
        {text}
      </pre>
    </div>
  );
};

const GuideStep: React.FC<{ index: number; step: SetupGuideStep }> = ({ index, step }) => (
  <li style={{ display: 'grid', gap: 8 }}>
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
      <span
        aria-hidden
        style={{
          flex: '0 0 auto',
          width: 22,
          height: 22,
          borderRadius: '50%',
          background: 'var(--z-accent)',
          color: 'var(--z-on-accent)',
          fontSize: 12,
          fontWeight: 700,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {index + 1}
      </span>
      <div style={{ display: 'grid', gap: 4 }}>
        <strong style={{ fontSize: 14 }}>{step.title}</strong>
        <span style={{ fontSize: 13, color: 'var(--z-muted)', lineHeight: 1.5 }}>
          {step.detail}
        </span>
        {step.href ? (
          <a
            href={step.href}
            target="_blank"
            rel="noreferrer"
            style={{ color: 'var(--z-accent)', fontSize: 13, fontWeight: 600 }}
          >
            {step.hrefLabel ?? step.href} ↗
          </a>
        ) : null}
      </div>
    </div>
    {step.code ? (
      <div style={{ marginLeft: 32 }}>
        <CopyableBlock label={step.codeLabel} text={step.code} />
      </div>
    ) : null}
  </li>
);

/** Step-by-step "how do I get my token" guide for one provider. */
export const ProviderSetupGuideModal: React.FC<ProviderSetupGuideModalProps> = ({
  source,
  isOpen,
  onClose,
}) => {
  const guide = providerSetupGuide(source);
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={guide.title}
      description={guide.intro}
      size="lg"
      footer={
        <Button variant="soft" size="sm" onClick={onClose}>
          Entendido
        </Button>
      }
    >
      <div
        data-testid="provider-setup-guide"
        data-source={source}
        style={{ display: 'grid', gap: 18, color: 'var(--z-text)' }}
      >
        <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 16 }}>
          {guide.steps.map((step, index) => (
            <GuideStep key={step.title} index={index} step={step} />
          ))}
        </ol>
        <p
          style={{
            margin: 0,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'var(--z-accent-soft)',
            color: 'var(--z-accent-copy)',
            fontSize: 12.5,
            lineHeight: 1.5,
          }}
        >
          {guide.permissionsNote}
        </p>
      </div>
    </Modal>
  );
};

export interface ProviderSetupGuideLinkProps {
  source: ConnectableSource;
  label?: string;
  testId?: string;
}

/** Inline trigger that owns the modal state; drop-in replacement for the old external links. */
export const ProviderSetupGuideLink: React.FC<ProviderSetupGuideLinkProps> = ({
  source,
  label = '¿Cómo obtengo mi token?',
  testId,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid={testId ?? `provider-setup-guide-link-${source}`}
        onClick={() => setOpen(true)}
        style={{
          alignSelf: 'flex-start',
          border: 0,
          padding: 0,
          background: 'none',
          color: 'var(--z-accent)',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {label}
      </button>
      <ProviderSetupGuideModal source={source} isOpen={open} onClose={() => setOpen(false)} />
    </>
  );
};
